import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Alert,
  Divider,
  Tabs,
  Grid
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ColumnHeightOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import ConditionBuilder from '../../../shared/components/cdn/ConditionBuilder';
import ActionBuilder from '../../../shared/components/cdn/ActionBuilder';

const { useBreakpoint } = Grid;

export default function PageRules() {
  const { domainData, reloadDomain } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [rules, setRules] = useState([]);
  const [form] = Form.useForm();
  const [matcher, setMatcher] = useState({});
  const [action, setAction] = useState({});

  useEffect(() => {
    loadRules();
  }, [domainData]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const response = await cdnService.getRules(domainData.id);

      if (response.success) {
        setRules(response.data || []);
      } else {
        message.error(response.error || '加载规则失败');
      }
    } catch (error) {
      console.error('Failed to load rules:', error);
      message.error('加载规则失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingRule(null);
    setMatcher({});
    setAction({});
    setModalVisible(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    const settings = rule.settings || {};

    form.setFieldsValue({
      name: rule.name,
      priority: rule.priority,
      enabled: rule.enabled !== undefined ? rule.enabled : true
    });

    setMatcher(settings.matcher || {});
    setAction(settings.action || {});
    setModalVisible(true);
  };

  const handleDelete = async (ruleId) => {
    setLoading(true);
    try {
      const response = await cdnService.deleteRule(ruleId);

      if (response.success) {
        message.success('删除成功');
        loadRules();
        reloadDomain();
      } else {
        message.error(response.error || '删除失败');
      }
    } catch (error) {
      console.error('Failed to delete rule:', error);
      message.error('删除失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (!matcher || !matcher.conditions || matcher.conditions.length === 0) {
        message.error('请至少配置一个匹配条件');
        setLoading(false);
        return;
      }
      // 请求头/Cookie/查询参数必须带名称、比较算子必须有值:否则边缘编译该规则失败并静默跳过。
      const badIdx = matcher.conditions.findIndex((c) => (
        (['HEADER', 'COOKIE', 'QS_ARGS'].includes(c.collection) && !String(c.collection_var || '').trim())
        || (!['exists', 'not_exists'].includes(c.op) && (Array.isArray(c.var) ? c.var.length === 0 : String(c.var ?? '').trim() === ''))
      ));
      if (badIdx >= 0) {
        const c = matcher.conditions[badIdx];
        message.error(['HEADER', 'COOKIE', 'QS_ARGS'].includes(c.collection) && !String(c.collection_var || '').trim()
          ? `第 ${badIdx + 1} 个条件：请填写参数名`
          : `第 ${badIdx + 1} 个条件：请填写值`);
        setLoading(false);
        return;
      }
      // 后端 RuleController 要求 action 非空，否则返回 400 原始报错。前置友好校验。
      if (!action || typeof action !== 'object' || Object.keys(action).length === 0) {
        message.error('请至少配置一个执行动作');
        setLoading(false);
        return;
      }

      const settings = {
        matcher: matcher,
        action: action
      };

      const data = {
        domain_id: domainData.id,
        name: values.name,
        priority: values.priority || 100,
        settings: settings,
        enabled: values.enabled !== undefined ? values.enabled : true
      };

      let response;
      if (editingRule) {
        response = await cdnService.updateRule({
          ...data,
          id: editingRule.id
        });
      } else {
        response = await cdnService.createRule(data);
      }

      if (response.success) {
        message.success(editingRule ? '更新成功' : '添加成功');
        setModalVisible(false);
        loadRules();
        reloadDomain();
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      console.error('Failed to save rule:', error);
      message.error('操作失败');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '规则名称',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      sorter: (a, b) => a.priority - b.priority,
      render: (priority) => <Tag color="blue">{priority}</Tag>
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '匹配条件',
      key: 'matcher',
      render: (_, record) => {
        const matcher = record.settings?.matcher || {};
        const conditionCount = matcher.conditions?.length || 0;
        return (
          <Space>
            <Tag color="blue">{conditionCount} 个条件</Tag>
            <Tag>{matcher.op === 'or' ? '任一满足' : '全部满足'}</Tag>
          </Space>
        );
      }
    },
    {
      title: '动作配置',
      key: 'action',
      ellipsis: true,
      render: (_, record) => {
        const action = record.settings?.action || {};
        const actions = Object.keys(action).filter(key => action[key] && typeof action[key] === 'object');
        return (
          <Space size={4} wrap>
            {actions.map(key => (
              <Tag key={key} color="cyan">{key}</Tag>
            ))}
            {actions.length === 0 && <Tag>无动作</Tag>}
          </Space>
        );
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条规则吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Mobile card renderer
  const renderMobileCard = (rule) => (
    <Card
      key={rule.id}
      size="small"
      style={{ marginBottom: 12 }}
      extra={
        <Tag color={rule.enabled ? 'success' : 'default'}>
          {rule.enabled ? '启用' : '禁用'}
        </Tag>
      }
    >
      <div style={{ marginBottom: 8 }}>
        <strong>{rule.name}</strong>
        <Tag color="blue" style={{ marginLeft: 8 }}>优先级: {rule.priority}</Tag>
      </div>
      <div style={{ marginBottom: 8 }}>
        <Space wrap size="small">
          <Tag color="blue">{rule.settings?.matcher?.conditions?.length || 0} 个条件</Tag>
          <Tag>{rule.settings?.matcher?.op === 'or' ? '任一满足' : '全部满足'}</Tag>
          {Object.keys(rule.settings?.action || {}).filter(key => rule.settings?.action?.[key] && typeof rule.settings?.action?.[key] === 'object').map(key => (
            <Tag key={key} color="cyan">{key}</Tag>
          ))}
        </Space>
      </div>
      <div style={{ fontSize: '12px', color: '#999', marginBottom: 8 }}>
        创建: {rule.created_at}
      </div>
      <Space wrap>
        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(rule)}>
          编辑
        </Button>
        <Popconfirm
          title="确定要删除这条规则吗?"
          onConfirm={() => handleDelete(rule.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button size="small" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      <Card
        title={
          <Space>
            <ColumnHeightOutlined />
            页面规则
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} size={isMobile ? 'small' : 'middle'}>
            {isMobile ? '添加' : '添加规则'}
          </Button>
        }
        size={isMobile ? 'small' : 'default'}
      >
        <Alert
          message="配置说明"
          description={isMobile ? "为不同URL配置缓存和优化设置" : "页面规则允许您为不同的 URL 模式配置不同的缓存、安全和优化设置。优先级数值越小，规则优先级越高。"}
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        {isMobile ? (
          <div>
            {rules.map(rule => renderMobileCard(rule))}
            {rules.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
                暂无规则
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={rules}
            rowKey="id"
            loading={loading}
            pagination={false}
          />
        )}
      </Card>

      {/* Add/Edit modal */}
      <Modal
        title={editingRule ? '编辑规则' : '添加规则'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={isMobile ? '100%' : 1200}
        style={{ top: isMobile ? 10 : 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            enabled: true,
            priority: 100
          }}
        >
          <Alert
            message="规则配置说明"
            description={isMobile ? "配置匹配条件和执行动作" : "页面规则由匹配条件和执行动作组成。当请求满足匹配条件时，将执行配置的动作。优先级数值越小，规则优先级越高。"}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            icon={<InfoCircleOutlined />}
          />

          <Form.Item
            name="name"
            label="规则名称"
            rules={[{ required: true, message: '请输入规则名称' }]}
          >
            <Input placeholder="例如: 静态资源缓存规则" />
          </Form.Item>

          <Space wrap style={{ width: '100%', marginBottom: 16 }}>
            <Form.Item name="priority" label="优先级" extra={isMobile ? undefined : "数值越小优先级越高"} style={{ marginBottom: 0 }}>
              <InputNumber min={1} max={999} style={{ width: isMobile ? 100 : 150 }} />
            </Form.Item>

            <Form.Item name="enabled" label="启用" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
          </Space>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>匹配条件</Divider>
          <ConditionBuilder value={matcher} onChange={setMatcher} />

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>执行动作</Divider>
          <ActionBuilder value={action} onChange={setAction} />
        </Form>
      </Modal>
    </div>
  );
}
